import {
  findPathToRoot,
  FocusData,
  HierarchyData,
} from "@mirage/mosaics/BaseProfilePage/OrgChart/helpers";
import * as d3 from "d3";
import { useCallback, useEffect, useMemo } from "react";

export const useData = ({
  focus,
  type,
  selection,
  overrideTree,
  expanded,
}: {
  focus: FocusData;
  type: "employee" | "team";
  selection?: FocusData;
  overrideTree?: HierarchyData;
  expanded: boolean;
}) => {
  const hierarchyData = useMemo(() => {
    const root = d3.hierarchy<HierarchyData>(overrideTree ?? focus);

    let maxDepth = 0;
    if (expanded || type === "team") {
      // Traverse the tree to find the maximum depth
      root.each((node) => {
        if (node.depth > maxDepth) {
          maxDepth = node.depth;
        }
      });
    }

    const nodes = root.descendants() as d3.HierarchyPointNode<HierarchyData>[];
    const links = root.links() as d3.HierarchyPointLink<HierarchyData>[];

    const pathToRoot = findPathToRoot(nodes, selection ?? focus);

    return { root, pathToRoot, nodes, links, maxDepth };
  }, [expanded, focus, overrideTree, selection, type]);

  return { hierarchyData };
};

export const useChartKeyboardShortcuts = ({
  expanded,
  adjustZoom,
  resetZoom,
}: {
  expanded: boolean;
  adjustZoom: (zoom: number) => void;
  resetZoom: (source: string) => void;
}) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!expanded) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key === "0") {
        event.preventDefault();
        resetZoom("keyboard");
      }

      if (
        (event.metaKey || event.ctrlKey) &&
        (event.key === "+" || event.key === "=")
      ) {
        event.preventDefault();
        adjustZoom(1.2);
      }

      if ((event.metaKey || event.ctrlKey) && event.key === "-") {
        event.preventDefault();
        adjustZoom(0.8);
      }
    },
    [adjustZoom, expanded, resetZoom],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
};
